import React from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { TaskDropdown } from '../TaskDropdown'
import { TagDropdownContent } from '../TagDropdown'
import type { Tag } from '@/src/renderer/lib/db/models'
import type { CollapsedDropdowns } from './hooks/useCollapsedDropdowns'

/** Owner id of the tags dropdown when opened from the "+N" overflow pill. */
const OVERFLOW_ID = '__overflow__'

interface CollapsedTagPillsProps {
  /** Pills to render (one entry when summarizing). */
  visibleTags: Tag[]
  /** Count behind the summary suffix / the "+N" overflow pill. */
  overflowCount: number
  /** Render ONE summary pill ("Work +2") instead of pills + "+N". */
  summarize: boolean
  /** All tag objects on the task — summary pill aria-label. */
  taskTags: Tag[]
  /** Selected tag ids as stored on the task (dropdown state + remove-all). */
  selectedIds: string[]
  show: boolean
  dropdowns: CollapsedDropdowns
  selectOnDropdownOpen: (open: boolean) => void
  saveTags: (tags: string[]) => void
  playSoftRemove: () => void
}

/**
 * The collapsed tag pills incl. their shared tags dropdown. Every pill
 * (regular, summary, "+N" overflow) opens the same TagDropdownContent —
 * only the trigger differs.
 */
export const CollapsedTagPills: React.FC<CollapsedTagPillsProps> = ({
  visibleTags, overflowCount, summarize, taskTags, selectedIds, show,
  dropdowns, selectOnDropdownOpen, saveTags, playSoftRemove,
}) => {
  const closeDropdown = () => {
    dropdowns.setTagsOpen(false)
    dropdowns.setActiveTagId(null)
  }

  const tagDropdownFor = (id: string, trigger: React.ReactElement) => (
    <TaskDropdown
      key={id}
      open={dropdowns.tagsOpen && dropdowns.activeTagId === id}
      onOpenChange={(open) => {
        dropdowns.setTagsOpen(open)
        dropdowns.setActiveTagId(open ? id : null)
        selectOnDropdownOpen(open)
      }}
      side="bottom"
      align="start"
      alignOffset={-16}
      trigger={trigger}
    >
      <TagDropdownContent
        placement="badge"
        value={selectedIds}
        onValueChange={saveTags}
        onClose={closeDropdown}
        onRemoveAll={() => {
          selectedIds.forEach((_, i) => setTimeout(() => playSoftRemove(), i * 80))
          saveTags([])
          closeDropdown()
        }}
      />
    </TaskDropdown>
  )

  return (
    <AnimatePresence>
    {show && (
      <motion.div
        className="task-entry__collapsed-tags"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.12 }}
        onClick={(e) => e.stopPropagation()}
        onDoubleClick={(e) => e.stopPropagation()}
      >
        {visibleTags.map(tag => tagDropdownFor(tag.id, (
          <button
            className="task-entry__tag-pill"
            type="button"
            onMouseEnter={() => dropdowns.switchTo('tags', tag.id)}
            aria-label={summarize && overflowCount > 0 ? taskTags.map(t => t.name).join(', ') : undefined}
          >
            {tag.name}{summarize && overflowCount > 0 ? ` +${overflowCount}` : ''}
          </button>
        )))}
        {!summarize && overflowCount > 0 && tagDropdownFor(OVERFLOW_ID, (
          <button
            className="task-entry__tag-pill task-entry__tag-pill--overflow"
            type="button"
            onMouseEnter={() => dropdowns.switchTo('tags', OVERFLOW_ID)}
          >
            +{overflowCount}
          </button>
        ))}
      </motion.div>
    )}
    </AnimatePresence>
  )
}

/**
 * Hidden natural-width measurers for the adaptive tag decision: the full
 * pill list and the summary variant, measured by useCollapsedRowFit
 * independently of what currently renders. Lives in __content (out of
 * flow, invisible, never interactive).
 */
export const TagPillMeasurers: React.FC<{ tags: Tag[] }> = ({ tags }) => {
  if (tags.length === 0) return null
  return (
    <div className="task-entry__tags-measure" aria-hidden="true">
      <div className="task-entry__tags-measure-item" data-tags-measure="full">
        {tags.map((tag) => (
          <button key={tag.id} className="task-entry__tag-pill" type="button" tabIndex={-1}>
            {tag.name}
          </button>
        ))}
      </div>
      {tags.length > 1 && (
        <div className="task-entry__tags-measure-item" data-tags-measure="summary">
          <button className="task-entry__tag-pill" type="button" tabIndex={-1}>
            {`${tags[0].name} +${tags.length - 1}`}
          </button>
        </div>
      )}
    </div>
  )
}
